import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, auth, fileUrl } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { LayoutDashboard, Users, Database, BookOpenCheck, Send, LogOut, HeartPulse, FileBarChart2, Settings as SettingsIcon, Plug, ShieldCheck, CalendarCheck2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import TopBar from "@/components/TopBar";

// Map nav items to permission page keys. View permission gates nav visibility.
const ALL_ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", tid: "nav-dashboard", pageKey: "dashboard" },
  { to: "/employees", icon: Users, label: "Employees", tid: "nav-employees", pageKey: "employees" },
  { to: "/questionnaires", icon: BookOpenCheck, label: "Quizzes", tid: "nav-questionnaires", pageKey: "questionnaires" },
  { to: "/assignments", icon: Send, label: "Assignments", tid: "nav-assignments", pageKey: "assignments" },
  { to: "/reports", icon: FileBarChart2, label: "Reports", tid: "nav-reports", pageKey: "reports" },
  { to: "/attendance", icon: CalendarCheck2, label: "Attendance", tid: "nav-attendance", pageKey: "attendance" },
  { to: "/events", icon: CalendarClock, label: "Events", tid: "nav-events", pageKey: "events" },
  { to: "/masters", icon: Database, label: "Masters", tid: "nav-masters", pageKey: "masters" },
  { to: "/security", icon: ShieldCheck, label: "Security", tid: "nav-security", pageKey: "security" },
  { to: "/settings", icon: SettingsIcon, label: "Branding", tid: "nav-settings", pageKey: "branding" },
  { to: "/settings/integrations", icon: Plug, label: "Integrations", tid: "nav-integrations", pageKey: "integrations" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const { branding, user } = useBranding();
  const brandColor = branding?.theme_color || "#0EA5E9";
  const [permData, setPermData] = useState(null);

  useEffect(() => {
    api.get("/me/permissions")
      .then((r) => setPermData(r.data))
      .catch(() => setPermData({ permissions: {}, role_name: user?.role }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const perms = permData?.permissions || {};
  const items = ALL_ITEMS.filter((i) => perms[i.pageKey]?.view);
  const roleLabel = permData?.role_name || user?.role || "";

  const logout = () => { auth.clear(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
          {branding?.logo_path ? (
            <img src={fileUrl(branding.logo_path)} alt="" className="h-10 w-10 rounded-xl object-cover"/>
          ) : (
            <div className="h-10 w-10 rounded-xl grid place-items-center text-white shadow-sm" style={{ background: brandColor }}>
              <HeartPulse className="h-5 w-5" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-base leading-tight text-slate-900 line-clamp-2">{branding?.hospital_name || "MedLearn"}</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 truncate mt-0.5">{branding?.tagline || "Hospital Training"}</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {permData === null ? (
            <div className="text-xs text-slate-400 px-3 py-2">Loading menu…</div>
          ) : items.length === 0 ? (
            <div className="text-xs text-slate-400 px-3 py-2">No accessible pages.</div>
          ) : items.map(({ to, icon: Icon, label, tid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  isActive
                    ? "text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
              style={({ isActive }) => isActive ? { background: brandColor } : undefined}
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          {user && (
            <div className="px-3 py-2 mb-1">
              <div className="text-xs font-bold text-slate-900 truncate">{user.name}</div>
              <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: brandColor }}>{roleLabel}</div>
            </div>
          )}
          <Button variant="ghost" onClick={logout} data-testid="logout-btn"
            className="w-full justify-start text-slate-600 hover:text-red-600">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <TopBar />
        <Outlet />
      </main>
    </div>
  );
}
